import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  Icon,
  Loader,
  TabContent,
  Timeline,
  type TimelineBlockProps,
  Typography,
} from '@saviynt/design-system';
import { format, isValid, parseISO } from 'date-fns';

import { CF_CRT_MED, CF_NEU_BLD } from '../../../../shared/constants';
import { useInViewObserver, useTypedSelector } from '../../../../shared/hooks';
import { type ServiceIdentityHistoryItem } from '../../../../shared/types';
import { useGetServiceIdHistory } from '../../hooks';
import { getTimelineProps } from '../../utils';

import './HistoryContent.css';

interface HistoryContentProps {
  activeTab: string;
  itemId: number | undefined;
  shouldRender: boolean;
}

/** Shape of the paginated history API response. */
interface HistoryApiResponse {
  data?: ServiceIdentityHistoryItem[];
  cursor?: string | null;
  message: string;
}

/**
 * Renders the History tab content for the service identity side panel,
 * displaying a timeline of past events with cursor-based infinite scroll.
 * Manages its own data fetching via useGetServiceIdHistory and uses
 * useInViewObserver to trigger loading the next page.
 * @param props - {@link HistoryContentProps}
 * @param props.activeTab - The currently active tab value.
 * @param props.itemId - The service identity ID used to fetch history.
 * @param props.shouldRender - Whether the side panel is currently rendered.
 * @returns The history tab content with a timeline or intermediate state.
 */
function HistoryContent({
  activeTab,
  itemId,
  shouldRender,
}: HistoryContentProps) {
  const { globalDateFormat } = useTypedSelector(
    (state) => state.savicopilot.global
  );

  // Pagination state
  const [historyData, setHistoryData] = useState<
    ServiceIdentityHistoryItem[] | null
  >(null);
  const [isObserverInView, setIsObserverInView] = useState(false);
  const nextCursorRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  // API hook
  const { handleFetchHistory, isHistoryError, isHistoryLoading } =
    useGetServiceIdHistory(itemId);

  const globalFormatWithTime = useMemo(
    () => `${globalDateFormat}, HH:mm`,
    [globalDateFormat]
  );

  const [hasMore, setHasMore] = useState(false);

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setIsObserverInView(isInView);
  });

  /**
   * Fetches history data. When `isReset` is true, clears existing data and
   * fetches the first page; otherwise appends the next page using the current cursor.
   */
  const triggerFetchHistory = useCallback(
    (isReset = false) => {
      if (isFetchingRef.current) {
        return;
      }

      if (!isReset && nextCursorRef.current === null) {
        return;
      }

      const cursor = isReset ? null : nextCursorRef.current;

      if (isReset) {
        setHistoryData(null);
        nextCursorRef.current = null;
        setHasMore(false);
      }

      isFetchingRef.current = true;

      handleFetchHistory(cursor)
        .then((response) => {
          const typedResponse = response as HistoryApiResponse;

          if (!typedResponse.data) {
            throw Error(typedResponse.message);
          }

          const newCursor = typedResponse.cursor ?? null;

          nextCursorRef.current = newCursor;
          setHasMore(newCursor !== null);

          setHistoryData((prev) =>
            isReset
              ? typedResponse.data ?? []
              : [...(prev ?? []), ...(typedResponse.data ?? [])]
          );
          setIsObserverInView(false);
        })
        .catch((error) => {
          if (isReset) {
            setHistoryData(null);
            nextCursorRef.current = null;
            setHasMore(false);
          }

          Logger.log('Failed fetching history ', error);
        })
        .finally(() => {
          isFetchingRef.current = false;
        });
    },
    [handleFetchHistory]
  );

  // Fetch first page when the History tab becomes active
  useEffect(() => {
    if (!itemId || !shouldRender || activeTab !== '2') {
      return;
    }

    triggerFetchHistory(true);
  }, [itemId, activeTab, shouldRender, triggerFetchHistory]);

  // Infinite scroll: fetch next page when observer comes into view
  useEffect(() => {
    if (!isObserverInView || !hasMore) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      triggerFetchHistory();
    }, 10);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(debounceTimer);
  }, [isObserverInView, hasMore, triggerFetchHistory]);

  const renderBody = useCallback(() => {
    if (isHistoryError && historyData === null) {
      return (
        <Box tag='div' className='ServiceIdentitySP-intermediateWrapper'>
          <Box tag='div' className='ServiceIdentitySP-errorMessageWrapper'>
            <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
            <Typography kind='body2' color={CF_CRT_MED}>
              <FormattedMessage
                id='DaArA8'
                defaultMessage="Data couldn't be retrieved."
                description='Error message shown when the history data fails to load in the service identity side panel'
              />
            </Typography>
          </Box>
          <Button
            kind='filled'
            size='small'
            onClick={() => triggerFetchHistory(true)}>
            <FormattedMessage
              id='iqkpnY'
              defaultMessage='Refresh'
              description='Refresh'
            />
          </Button>
        </Box>
      );
    }

    // Initial loading (no data yet)
    if (isHistoryLoading && historyData === null) {
      return (
        <Box tag='div' className='ServiceIdentitySP-intermediateWrapper'>
          <Loader kind='dots' size='small' format='inline' color='brand' />
        </Box>
      );
    }

    if (historyData !== null && historyData.length === 0) {
      return (
        <Box tag='div' className='ServiceIdentitySP-intermediateWrapper'>
          <Box tag='div' className='ServiceIdentitySP-errorMessageWrapper'>
            <Icon kind='AlertInfo' size='large' />
            <Typography kind='body2' color={CF_NEU_BLD}>
              <FormattedMessage
                id='WX2Btk'
                defaultMessage=' No data found.'
                description='Message shown when the history data loads successfully but there are no history items to display in the service identity side panel'
              />
            </Typography>
          </Box>
        </Box>
      );
    }

    if (!historyData) {
      return null;
    }

    const timeLineData: TimelineBlockProps[] = historyData.map((h) => {
      const { colorTheme, icon } = getTimelineProps(h.eventType);

      return {
        title: h.eventType,
        attributes: h.changeLog.map((c) => {
          // Ensure UTC dates from the API are parsed correctly so
          // date-fns format() converts them to the local timezone.
          const utcValue = c.value.endsWith('Z') ? c.value : `${c.value}Z`;
          const parsed = parseISO(utcValue);

          return {
            label: c.key,
            detail: isValid(parsed)
              ? format(parsed, globalFormatWithTime)
              : c.value,
          };
        }),
        connectorKind: 'past',
        medallionKind: 'filled',
        colorTheme,
        icon,
      };
    });

    return (
      <>
        <Timeline timelineBlocksData={timeLineData} />
        {hasMore && listObserverElement}
        {hasMore && isHistoryLoading && (
          <Box
            tag='div'
            className='ServiceIdentitySP-intermediateWrapper ServiceIdentitySP-intermediateWrapper--isScrollLoad'>
            <Loader kind='dots' size='small' format='inline' color='brand' />
          </Box>
        )}
      </>
    );
  }, [
    historyData,
    isHistoryLoading,
    isHistoryError,
    hasMore,
    globalFormatWithTime,
    triggerFetchHistory,
    listObserverElement,
  ]);

  return (
    <TabContent
      className='HistoryContent-wrapperContainer'
      value={activeTab}
      tabValue='2'>
      {renderBody()}
    </TabContent>
  );
}

export default memo(HistoryContent);
